import { useMemo } from 'react';

import {
    useRebillRevenueData,
    useRefundsData,
    useStraightSaleData,
    useUpsellRevenueData,
    useActiveSubscriptionsData,
    useSubscriptionsGainedData,
    useSubscriptionsCancelledData,
    useApprovalRatesData,
    useAverageLTVData,
    useSubscriptionsByStatusData,
    useSubscriptionsByStatusTimeSeriesData,
    useUpsellTakeRateData,
    useRevenueByProcessorData,
    useTransactionErrorsData,
    useRefundsByTypeData,
    useRevenueByProcessorTimeSeriesData,
    useTransactionErrorsTimeSeriesData,
    useCustomerGeographicByCardCountryData,
    useCustomerGeographicByIpCountryData,
    useProductsByRevenueData,
    useVariantsByRevenueData,
    useTopSellingProductsData,
    useProductSalesOverTimeData,
    type AnalyticsFilters,
} from '@/lib/api/analytics';
import { useStatsFilters } from '../context/StatsFiltersContext';

export function useAnalyticsFast(params: {
    startDate?: Date;
    endDate?: Date;
    storeIds?: string[];
    currency?: string;
    enabled?: boolean;
}) {
    const { selectedDates, selectedStores, selectedCurrency } = useStatsFilters();

    const startDate = params.startDate ?? selectedDates.from;
    const endDate = params.endDate ?? selectedDates.to;
    const currency = params.currency ?? selectedCurrency;
    const storeIds = params.storeIds ?? selectedStores;

    const enabled = params.enabled !== false && !!(startDate && endDate);

    const filters: AnalyticsFilters = {
        startDate: startDate!,
        endDate: endDate!,
        storeIds,
        currency,
    };

    // All hooks run in parallel
    const rebillRevenueQuery = useRebillRevenueData(filters, { enabled });
    const refundsQuery = useRefundsData(filters, { enabled });
    const straightSaleQuery = useStraightSaleData(filters, { enabled });
    const upsellRevenueQuery = useUpsellRevenueData(filters, { enabled });
    const activeSubscriptionsQuery = useActiveSubscriptionsData(filters, { enabled });
    const subscriptionsGainedQuery = useSubscriptionsGainedData(filters, { enabled });
    const subscriptionsCancelledQuery = useSubscriptionsCancelledData(filters, { enabled });
    const approvalRatesQuery = useApprovalRatesData(filters, { enabled });
    const averageLTVQuery = useAverageLTVData(filters, { enabled });
    const subscriptionsByStatusQuery = useSubscriptionsByStatusData(filters, { enabled });
    const subscriptionsByStatusTimeSeriesQuery = useSubscriptionsByStatusTimeSeriesData(filters, { enabled });
    const upsellTakeRateQuery = useUpsellTakeRateData(filters, { enabled });
    const revenueByProcessorQuery = useRevenueByProcessorData(filters, { enabled });
    const transactionErrorsQuery = useTransactionErrorsData(filters, { enabled });
    const refundsByTypeQuery = useRefundsByTypeData(filters, { enabled });
    const revenueByProcessorTimeSeriesQuery = useRevenueByProcessorTimeSeriesData(filters, { enabled });
    const transactionErrorsTimeSeriesQuery = useTransactionErrorsTimeSeriesData(filters, { enabled });
    const customerGeographicByCardCountryQuery = useCustomerGeographicByCardCountryData(filters, { enabled });
    const customerGeographicByIpCountryQuery = useCustomerGeographicByIpCountryData(filters, { enabled });
    const productsByRevenueQuery = useProductsByRevenueData(filters, { enabled });
    const variantsByRevenueQuery = useVariantsByRevenueData(filters, { enabled });
    const topSellingProductsQuery = useTopSellingProductsData(filters, { enabled });
    const productSalesOverTimeQuery = useProductSalesOverTimeData(filters, { enabled });

    // Convert objects back to Maps (TRPC returns objects, components expect Maps)
    const data = useMemo(() => {
        if (!enabled) return null;

        return {
            rebillRevenueByDate: rebillRevenueQuery.data ? new Map(Object.entries(rebillRevenueQuery.data)) : undefined,
            refundsByDate: refundsQuery.data ? new Map(Object.entries(refundsQuery.data)) : undefined,
            straightSaleRevenueByDate: straightSaleQuery.data ? new Map(Object.entries(straightSaleQuery.data)) : undefined,
            upsellRevenueByDate: upsellRevenueQuery.data ? new Map(Object.entries(upsellRevenueQuery.data)) : undefined,
            activeSubscriptionsByDate: activeSubscriptionsQuery.data ? new Map(Object.entries(activeSubscriptionsQuery.data)) : undefined,
            subscriptionsGainedByDate: subscriptionsGainedQuery.data ? new Map(Object.entries(subscriptionsGainedQuery.data)) : undefined,
            subscriptionsCancelledByDate: subscriptionsCancelledQuery.data ? new Map(Object.entries(subscriptionsCancelledQuery.data)) : undefined,
            approvalRatesByBillingCycle: approvalRatesQuery.data,
            averageLTVData: averageLTVQuery.data,
            subscriptionsByStatus: subscriptionsByStatusQuery.data,
            subscriptionsByStatusTimeSeries: subscriptionsByStatusTimeSeriesQuery.data ? new Map(Object.entries(subscriptionsByStatusTimeSeriesQuery.data)) : undefined,
            upsellTakeRateByDate: upsellTakeRateQuery.data ? new Map(Object.entries(upsellTakeRateQuery.data)) : undefined,
            revenueByProcessor: revenueByProcessorQuery.data,
            transactionErrors: transactionErrorsQuery.data,
            refundsByType: refundsByTypeQuery.data ? new Map(Object.entries(refundsByTypeQuery.data)) : undefined,
            revenueByProcessorTimeSeries: revenueByProcessorTimeSeriesQuery.data,
            transactionErrorsTimeSeries: transactionErrorsTimeSeriesQuery.data,
            customerGeographicByCardCountry: customerGeographicByCardCountryQuery.data,
            customerGeographicByIpCountry: customerGeographicByIpCountryQuery.data,
            productsByRevenue: productsByRevenueQuery.data,
            variantsByRevenue: variantsByRevenueQuery.data,
            topSellingProducts: topSellingProductsQuery.data,
            productSalesOverTime: productSalesOverTimeQuery.data ? new Map(Object.entries(productSalesOverTimeQuery.data)) : undefined,
        };
    }, [
        rebillRevenueQuery.data,
        refundsQuery.data,
        straightSaleQuery.data,
        upsellRevenueQuery.data,
        activeSubscriptionsQuery.data,
        subscriptionsGainedQuery.data,
        subscriptionsCancelledQuery.data,
        approvalRatesQuery.data,
        averageLTVQuery.data,
        subscriptionsByStatusQuery.data,
        subscriptionsByStatusTimeSeriesQuery.data,
        upsellTakeRateQuery.data,
        revenueByProcessorQuery.data,
        transactionErrorsQuery.data,
        refundsByTypeQuery.data,
        revenueByProcessorTimeSeriesQuery.data,
        transactionErrorsTimeSeriesQuery.data,
        customerGeographicByCardCountryQuery.data,
        customerGeographicByIpCountryQuery.data,
        productsByRevenueQuery.data,
        variantsByRevenueQuery.data,
        topSellingProductsQuery.data,
        productSalesOverTimeQuery.data,
        enabled,
    ]);

    // Individual loading states
    const loadingStates = useMemo(() => ({
        rebillRevenue: rebillRevenueQuery.isLoading,
        refunds: refundsQuery.isLoading,
        straightSale: straightSaleQuery.isLoading,
        upsellRevenue: upsellRevenueQuery.isLoading,
        activeSubscriptions: activeSubscriptionsQuery.isLoading,
        subscriptionsGained: subscriptionsGainedQuery.isLoading,
        subscriptionsCancelled: subscriptionsCancelledQuery.isLoading,
        approvalRates: approvalRatesQuery.isLoading,
        averageLTV: averageLTVQuery.isLoading,
        subscriptionsByStatus: subscriptionsByStatusQuery.isLoading,
        subscriptionsByStatusTimeSeries: subscriptionsByStatusTimeSeriesQuery.isLoading,
        upsellTakeRate: upsellTakeRateQuery.isLoading,
        revenueByProcessor: revenueByProcessorQuery.isLoading,
        transactionErrors: transactionErrorsQuery.isLoading,
        refundsByType: refundsByTypeQuery.isLoading,
        revenueByProcessorTimeSeries: revenueByProcessorTimeSeriesQuery.isLoading,
        transactionErrorsTimeSeries: transactionErrorsTimeSeriesQuery.isLoading,
        customerGeographicByCardCountry: customerGeographicByCardCountryQuery.isLoading,
        customerGeographicByIpCountry: customerGeographicByIpCountryQuery.isLoading,
        productsByRevenue: productsByRevenueQuery.isLoading,
        variantsByRevenue: variantsByRevenueQuery.isLoading,
        topSellingProducts: topSellingProductsQuery.isLoading,
        productSalesOverTime: productSalesOverTimeQuery.isLoading,
    }), [
        rebillRevenueQuery.isLoading,
        refundsQuery.isLoading,
        straightSaleQuery.isLoading,
        upsellRevenueQuery.isLoading,
        activeSubscriptionsQuery.isLoading,
        subscriptionsGainedQuery.isLoading,
        subscriptionsCancelledQuery.isLoading,
        approvalRatesQuery.isLoading,
        averageLTVQuery.isLoading,
        subscriptionsByStatusQuery.isLoading,
        subscriptionsByStatusTimeSeriesQuery.isLoading,
        upsellTakeRateQuery.isLoading,
        revenueByProcessorQuery.isLoading,
        transactionErrorsQuery.isLoading,
        refundsByTypeQuery.isLoading,
        revenueByProcessorTimeSeriesQuery.isLoading,
        transactionErrorsTimeSeriesQuery.isLoading,
        customerGeographicByCardCountryQuery.isLoading,
        customerGeographicByIpCountryQuery.isLoading,
        productsByRevenueQuery.isLoading,
        variantsByRevenueQuery.isLoading,
        topSellingProductsQuery.isLoading,
        productSalesOverTimeQuery.isLoading,
    ]);

    // Overall loading state (any query is loading)
    const isLoading = Object.values(loadingStates).some(loading => loading);

    // Collect errors
    const error = rebillRevenueQuery.error ||
                  refundsQuery.error ||
                  straightSaleQuery.error ||
                  upsellRevenueQuery.error ||
                  activeSubscriptionsQuery.error ||
                  subscriptionsGainedQuery.error ||
                  subscriptionsCancelledQuery.error ||
                  approvalRatesQuery.error ||
                  averageLTVQuery.error ||
                  subscriptionsByStatusQuery.error ||
                  subscriptionsByStatusTimeSeriesQuery.error ||
                  upsellTakeRateQuery.error ||
                  revenueByProcessorQuery.error ||
                  transactionErrorsQuery.error ||
                  refundsByTypeQuery.error ||
                  revenueByProcessorTimeSeriesQuery.error ||
                  transactionErrorsTimeSeriesQuery.error ||
                  customerGeographicByCardCountryQuery.error ||
                  customerGeographicByIpCountryQuery.error ||
                  productsByRevenueQuery.error ||
                  variantsByRevenueQuery.error ||
                  topSellingProductsQuery.error ||
                  productSalesOverTimeQuery.error;

    return {
        data,
        isLoading,
        loadingStates,
        error,
    };
}
